import { ExternalLink, Mail } from 'lucide-react';
import type { SupportPartnerDto } from '@/api/control/missionImpactAPI.schemas';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HELP_CENTER_URLS } from '@/constants/helpCenter';
import { useLanguage } from '@/contexts/LanguageContext';
import { fillSupportTemplate } from '@/lib/support';

interface SupportCoverageCardProps {
  /** The organization's partner, or null when none is assigned. */
  partner: SupportPartnerDto | null;
}

interface CoverageSectionProps {
  heading: string;
  tag: string;
  tagClassName: string;
  items: string[];
}

/**
 * One group in the coverage card: who helps, a tag saying how it's paid for, and what they cover.
 */
function CoverageSection({ heading, tag, tagClassName, items }: CoverageSectionProps) {
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{heading}</h3>
        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${tagClassName}`}>{tag}</span>
      </div>
      <ul className="ml-4 list-disc space-y-1 text-sm text-muted-foreground">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Explains who helps with what before a ticket is raised. Data Rodeo support always covers the
 * product; setup and services go to the organization's partner when one is assigned, or to a
 * Data Wrangler using the organization's hours when not.
 */
export function SupportCoverageCard({ partner }: SupportCoverageCardProps) {
  const { t, language } = useLanguage();
  const coverage = t.support.coverage;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{coverage.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <CoverageSection
          heading={coverage.supportHeading}
          tag={coverage.included}
          tagClassName="border-success background-success text-success"
          items={[coverage.supportBugs, coverage.supportProduct, coverage.supportKnowledgeBase]}
        />
        {partner ? (
          <div className="space-y-2">
            <CoverageSection
              heading={partner.name ?? coverage.partnerTag}
              tag={coverage.partnerTag}
              tagClassName="border-info background-info text-info"
              items={[coverage.partnerSetup, coverage.partnerProcesses, coverage.partnerServices]}
            />
            {partner.primaryEmail && (
              <a
                href={`mailto:${partner.primaryEmail}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-4"
              >
                <Mail className="h-3.5 w-3.5" aria-hidden />
                {fillSupportTemplate(coverage.contactPartner, { name: partner.name ?? '' })}
              </a>
            )}
          </div>
        ) : (
          <CoverageSection
            heading={coverage.wranglerHeading}
            tag={coverage.paidHours}
            tagClassName="border-warning background-warning text-warning"
            items={[coverage.wranglerSetup, coverage.wranglerHandsOn]}
          />
        )}
        <a
          href={HELP_CENTER_URLS[language]}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-4"
        >
          {coverage.knowledgeBase}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      </CardContent>
    </Card>
  );
}
